import {Injectable} from '@angular/core';
import {Temporal} from '@js-temporal/polyfill';
import PlainDate = Temporal.PlainDate;
import Instant = Temporal.Instant;
import ZonedDateTime = Temporal.ZonedDateTime;

// https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
export type TimeZoneType = 'BE';
export type TimeZoneValue = 'Europe/Brussels';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  private readonly timeZones: Map<TimeZoneType, TimeZoneValue> = new Map([
    ['BE', 'Europe/Brussels'],
  ]);

  public today(timeZoneType: TimeZoneType = 'BE'): PlainDate {
    return Temporal.Now.plainDateISO(this.getTimeZone(timeZoneType));
  }

  public addDays(date: PlainDate, days: number): PlainDate {
    return date.add({days});
  }

  public subtractDays(date: PlainDate, days: number): PlainDate {
    return date.subtract({days});
  }

  public toZonedDateTime(instant: Instant, timeZoneType: TimeZoneType = 'BE'): ZonedDateTime {
    return instant.toZonedDateTimeISO(this.getTimeZone(timeZoneType));
  }

  public toPlainDate(instant: Instant, timeZoneType: TimeZoneType = 'BE'): PlainDate {
    return this.toZonedDateTime(instant, timeZoneType).toPlainDate();
  }

  public fromISO(isoString: string): PlainDate {
    return PlainDate.from(isoString);
  }

  public fromDate(date: Date, timeZoneType: TimeZoneType = 'BE'): PlainDate {
    return Temporal.Instant.fromEpochMilliseconds(date.getTime())
      .toZonedDateTimeISO(this.getTimeZone(timeZoneType))
      .toPlainDate();
  }

  public toDate(date: PlainDate, timeZoneType: TimeZoneType = 'BE'): Date {
    return new Date(date.toZonedDateTime(this.getTimeZone(timeZoneType)).epochMilliseconds);
  }

  private compare(a: PlainDate, b: PlainDate): number {
    return PlainDate.compare(a, b);
  }

  public isBefore(a: PlainDate, b: PlainDate): boolean {
    return PlainDate.compare(a, b) < 0;
  }

  public isAfter(a: PlainDate, b: PlainDate): boolean {
    return PlainDate.compare(a, b) > 0;
  }

  public isSameOrBefore(a: PlainDate, b: PlainDate): boolean {
    return PlainDate.compare(a, b) <= 0;
  }

  public isSameOrAfter(a: PlainDate, b: PlainDate): boolean {
    return PlainDate.compare(a, b) >= 0;
  }

  public getMinutesDifference(startTime: Date, endTime: Date): number {
    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
    return endMinutes - startMinutes;
  }

  public addMinutesToTime(time: Date, minutes: number): Date {
    const result = new Date(time);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
  }

  private getTimeZone(timeZoneType: TimeZoneType): TimeZoneValue {
    return this.timeZones.get(timeZoneType)!;
  }
}
